import React, { createContext, useReducer, ReactNode, useCallback, useEffect, useContext } from 'react'
import {
  JournalEntriesState,
  JournalEntriesFilters,
  journalEntriesInitialState,
  JournalEntriesActionType,
  JournalEntriesAction
} from '../interfaceTypes/journalEntries'
import { journalEntriesReducer } from '../components/accounting/journalEntries/journalEntriesReducer'
import { useFetchJournalEntriesLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT } from '../constants'
import { AuthContext } from './AuthContext'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'
import { formatAsEndOfDay, formatAsStartOfDay } from '../utils'
import { EntryFilterType } from '../interfaceTypes/journalEntries'

interface JournalEntriesContextType extends JournalEntriesState {
  dispatch: (action: JournalEntriesAction) => void
  refetch: () => void
  setFilters: (filters: JournalEntriesFilters) => void
}

export const JournalEntriesContext = createContext<JournalEntriesContextType>({
  ...journalEntriesInitialState,
  dispatch: () => { },
  refetch: () => { },
  setFilters: () => { }
})

interface JournalEntriesProviderProps {
  children: ReactNode
}

export const JournalEntriesContextProvider: React.FC<JournalEntriesProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(journalEntriesReducer, journalEntriesInitialState)
  const { isLoggedIn } = useContext(AuthContext)
  const { currentPage, dispatch: appDispatch } = useContext(AppContext)

  const [fetchJournalEntries, { loading, refetch }] = useFetchJournalEntriesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError: () => {
      dispatch({ type: JournalEntriesActionType.SET_JOURNAL_ENTRIES, journalEntries: [] })
      dispatch({ type: JournalEntriesActionType.SET_LOADING, loading: false })
    },
    onCompleted: data => {
      if (data?.fetchJournalEntries?.journalEntries) {
        dispatch({
          type: JournalEntriesActionType.SET_JOURNAL_ENTRIES,
          journalEntries: data.fetchJournalEntries.journalEntries
        })
      }

      if (data?.fetchJournalEntries?.pagination) {
        const { totalPages, totalCount } = data.fetchJournalEntries.pagination
        totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
        totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
      }

      dispatch({ type: JournalEntriesActionType.SET_LOADING, loading: false })
    }
  })

  const setFilters = useCallback(
    (filters: JournalEntriesFilters) => {
      dispatch({ type: JournalEntriesActionType.SET_FILTERS, filters })
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    },
    [appDispatch]
  )

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: JournalEntriesActionType.SET_LOADING, loading: true })
      const startDate = state.filters.startDate ? formatAsStartOfDay(state.filters.startDate) : undefined
      const endDate = state.filters.endDate ? formatAsEndOfDay(state.filters.endDate) : undefined

      fetchJournalEntries({
        variables: {
          input: {
            accountId: state.filters.account?.value,
            startDate,
            endDate,
            excludeSystemGenerated: state.filters.type === EntryFilterType.MANUAL,
            excludeManual: state.filters.type === EntryFilterType.SYSTEM,
            searchTerm: state.filters.searchTerm,
            page: currentPage,
            limit: PAGE_LIMIT
          }
        }
      })
    }
  }, [state.filters, currentPage, isLoggedIn, appDispatch, fetchJournalEntries])

  const contextValue: JournalEntriesContextType = {
    ...state,
    loading: loading || state.loading,
    dispatch,
    refetch,
    setFilters
  }

  return <JournalEntriesContext.Provider value={contextValue}>{children}</JournalEntriesContext.Provider>
}
